import { useState, useContext, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';

import { MENU_RICH_STYLE } from '@/app-configuration';
import { Default, Mobile } from '@helpers/breakpoints';
import PageContext from '@contexts/PageContext';
import { Container, Button } from '@components';
import { checkEnabled,checkValue } from '@components/OptionalFeature';
import { threeSixtySelector } from '@stores/compareProduct/selectors';
import { ThreeSixtyViewer } from '@stores/compareProduct/actions';
import { useMenuSelector } from '@stores/menu/selectors';
import MenuService from '@services/menuService';
import ThreeSixtyIcon from '@assets/icon/360.svg';
import styles from './SubMenu.module.scss';

const SubMenu = ({ data, DarkMode, id, componentIsAvailable }) => {
  const pageData = useContext(PageContext);
  const dispatch = useDispatch();
  const router = useRouter();
  const isEnabledThreeSixty = useSelector(threeSixtySelector);
  const menuItems = useSelector(useMenuSelector);
  const { locale } = router;
  const [openDropDown, setOpenDropDown] = useState(false);
  const [anchorTexts, setAnchorTexts] = useState([{}]);
  const [isDoubleHeaderMenu, setIsDoubleHeaderMenu] = useState(false);

  const dropDownHandler = () => {
    setOpenDropDown(!openDropDown);
  };
  const ItemClickedHandler = () =>{
    setOpenDropDown(!openDropDown);
  }

  const checkDisplayOptions = () => {
    const sectionsAnchorText = pageData?.DisplayOptions.map((sec) => {
      return { name: sec.Name, displayOptions: sec.DisplayOptions };
    }).map((item) => ({
      name: item.name,
      frenchEnabled : item.displayOptions.find((x) => x.DisplayOptionKey === 'DisplayComponent')?.OptionInformation,
      enabled: item.displayOptions.find((x) => x.DisplayOptionKey === 'DisplayComponent')?.Enabled,
      anchorTextEnabled: item.displayOptions.find((x) => x.DisplayOptionKey === 'SubMenuAnchorText')?.Enabled,
      anchorText: locale=='fr' ? item.displayOptions.find((x) => x.DisplayOptionKey === 'SubMenuAnchorText')?.OptionInformation?.fr : item.displayOptions.find((x) => x.DisplayOptionKey === 'SubMenuAnchorText')?.OptionInformation?.en
    }));
    setAnchorTexts(sectionsAnchorText);
  };

  const anchorTextsArr = anchorTexts
    .filter((sec) => sec.enabled === true && sec.anchorTextEnabled === true)
    .map((x) => x.anchorText || x.name);

  // check Display360ViewerButton is Enabled
  const subMenu360Btn = checkEnabled(data?.DisplayOptions, 'Display360ViewerButton');

  // check DisplayRequestAQuoteButton is Enabled
  const subMenuQuoteBtn = checkEnabled(data?.DisplayOptions, 'DisplayRequestAQuoteButton');

  // check title Enabled
  const titleAvailable = checkEnabled(data?.DisplayOptions, 'Title');

  // check Build Your Own button
  const BuildYourOwnEnabled = checkEnabled(data?.DisplayOptions, 'BuildYourOwnButtonTarget');
  const BuildYourOwnText = checkValue(data?.DisplayOptions, 'BuildYourOwnButtonText', locale) ;
  const BuildYourOwnTextEnabled = checkEnabled(data?.DisplayOptions, 'BuildYourOwnButtonText') ;
  const BuildYourOwnButtonUrl = checkValue(data?.DisplayOptions, 'BuildYourOwnButtonUrl', locale);
  const BuildYourOwnUrl = BuildYourOwnButtonUrl || `/buildmykubota/${data.ProductId}`;


  const handle360 = () => {
    dispatch(ThreeSixtyViewer(!isEnabledThreeSixty));
  };

  useEffect(() => {
    dispatch(ThreeSixtyViewer(false));
    checkDisplayOptions();
  }, []);

  // check header menu
  useEffect(() => {
    const fetchMenus = async () => {
      const menu = await MenuService().getMenuData();
      setIsDoubleHeaderMenu(menuItems[locale]?.filter((menuItem) => menuItem.id !== 1)?.length > 1);
    }
    fetchMenus();
  }, []);

  return (
    componentIsAvailable && (
      <section
        className={classNames(styles.Section, {
          [styles.DarkMode]: DarkMode,
          [styles.DoubleMode]: isDoubleHeaderMenu,
        })}
        id={id}
      >
        <Container className={styles.Container}>
          <div className={styles.SubMenuContainer}>
            <Mobile>
              <span onClick={dropDownHandler}>
                {titleAvailable ? pageData.ProductText.Title : null}
                <FontAwesomeIcon icon={faAngleDown} />
              </span>
              <AnimatePresence>
                {openDropDown && (
                  <motion.div
                    className={classNames(styles.MenuDrawer, {
                      [styles.RichMenuDrawer]: MENU_RICH_STYLE,
                    })}
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: {
                        opacity: 1,
                        height: 'auto',
                        transition: { delayChildren: 0.2 },
                        width: '100%'
                      },
                      collapsed: {
                        opacity: 0,
                        height: 0,
                        transition: { when: 'afterChildren' }
                      }
                    }}
                    transition={{
                      ease: 'easeInOut',
                      duration: 0.4
                    }}
                  >
                    <ul>
                      {anchorTextsArr.length > 0 && anchorTextsArr.map((name, index) => (
                        <li key={index} onClick={ItemClickedHandler}>
                          <a href={`#${name?.match(/compatible/i) ? 'CompatibleImplements' : name}`}>
                            {name}
                          </a>
                        </li>
                      ))}
                      {subMenu360Btn && (
                        <li className={styles.border}>
                          <a href={'#' + 'Overview'}>
                            360 Viewer <ThreeSixtyIcon />
                          </a>
                        </li>
                      )}
                      {subMenuQuoteBtn && (
                        <li className={styles.Button}>
                          <a href={`/dealer/contact-a-dealer?product=${pageData.Id}`}>
                            {locale == "fr" ? 'Demander un devis' : 'Request A Quote'}
                          </a>
                        </li>
                      )}
                      {BuildYourOwnEnabled && (
                        <li className={styles.Button}>
                          <a href={`/buildmykubota/${pageData.Id}`}>
                            {locale == "fr" ? 'Construisez mon Kubota' : 'Build My Kubota'}
                          </a>
                        </li>
                      )}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </Mobile>

            <Default>
              {titleAvailable && <span>{pageData.ProductText.Title}</span>}
              <ul>
                {(anchorTextsArr || []).map((name, index) => (
                  <li key={index}>
                    <a href={`#${name?.match(/compatible/i) ? 'CompatibleImplements' : name}`}>
                      {name}
                    </a>
                  </li>
                ))}
              </ul>
              <ul className={styles.ButtonSec}>
                {subMenu360Btn && (
                  <Button animated onClick={handle360} borderRadius className={styles.viewer}>
                    <a href={'#' + 'Overview'}>
                      360 Viewer <ThreeSixtyIcon style={{ marginLeft: 10 }} />
                    </a>
                  </Button>
                )}
                {subMenuQuoteBtn && (
                  <Button borderRadius className={styles.Button} href={`/dealer/contact-a-dealer?product=${pageData.Id}`} animated>
                    {locale == "fr"? 'Demander un devis' : 'Request A Quote'}
                  </Button>
                )}
                {BuildYourOwnEnabled && (
                  <Button borderRadius className={styles.Button} href={`/buildmykubota/${pageData.Id}`} animated>
                    {BuildYourOwnTextEnabled ?(
                    <>                     
                      {BuildYourOwnText}   
                    </>
                    ): (
                      <>
                      {locale == "fr"? 'Construisez mon Kubota' : 'Build My Kubota'}
                      </>
                    )}
                  </Button>
                )}
              </ul>
            </Default>
          </div>
        </Container>
      </section>
    )
  );
};

export default SubMenu;
